<template>
  <nav class="pagination mt-10">
    <ol
      class="list-nostyle flex md:gap-3 items-start justify-center text-sm md:text-base"
    >
      <li class="!flex">
        <button
          type="button"
          :disabled="currentPage === 1"
          class="py-1 px-2"
          @click.prevent="pagechanged(1)"
        >
          <NuxtIcon
            name="chevron-line"
            filled
            class="chevron-icon prev"
            :class="currentPage === 1 ? 'disabled' : 'active'"
          />
        </button>
        <button
          type="button"
          :disabled="currentPage === 1"
          class="py-1 px-2"
          @click.prevent="previousPage"
        >
          <NuxtIcon
            name="arrow"
            filled
            class="arrow-icon prev"
            :class="currentPage === 1 ? 'disabled' : 'active'"
          />
        </button>
      </li>
      <template v-if="!showDots">
        <li v-for="(page, pi) in pages()" :key="pi">
          <button
            type="button"
            :disabled="page.isDisabled"
            class="py-1 px-2 text-[#BCBBBB] hover:border-primary-primary hover:border-b-2 outline-none hover:font-medium hover:text-bw-900"
            :class="
              currentPage === page.name
                ? 'border-b-2 border-primary-primary text-bw-900 font-medium'
                : 'border-b-2 border-transparent'
            "
            @click.prevent="pagechanged(page.name)"
          >
            {{ page.name }}
          </button>
        </li>
      </template>
      <template v-else>
        <li v-for="(page, pi) in firstThreePages()" :key="pi">
          <button
            type="button"
            :disabled="page.isDisabled"
            class="py-1 px-2 text-[#BCBBBB] hover:border-primary-primary hover:border-b-2 outline-none hover:font-medium hover:text-bw-900"
            :class="
              currentPage === page.name
                ? 'border-b-2 border-primary-primary text-bw-900 font-medium'
                : 'border-b-2 border-transparent'
            "
            @click.prevent="pagechanged(page.name)"
          >
            {{ page.name }}
          </button>
        </li>
        <li>
          <button type="button" class="py-1 px-2 text-[#BCBBBB] cursor-default">
            ...
          </button>
        </li>
        <li v-for="(page, pi) in lastTwoPages()" :key="pi">
          <button
            type="button"
            :disabled="page.isDisabled"
            class="py-1 px-2 text-[#BCBBBB] hover:border-primary-primary hover:border-b-2 outline-none hover:font-medium hover:text-bw-900"
            :class="
              currentPage === page.name
                ? 'border-b-2 border-primary-primary text-bw-900 font-medium'
                : 'border-b-2 border-transparent'
            "
            @click.prevent="pagechanged(page.name)"
          >
            {{ page.name }}
          </button>
        </li>
      </template>
      <li class="!flex">
        <button
          type="button"
          :disabled="currentPage === totalPages"
          class="py-1 px-2"
          @click.prevent="nextPage"
        >
          <NuxtIcon
            name="arrow"
            filled
            class="arrow-icon"
            :class="currentPage === totalPages ? 'disabled' : 'active'"
          />
        </button>
        <button
          type="button"
          :disabled="currentPage === totalPages"
          class="py-1 px-2"
          @click.prevent="pagechanged(totalPages)"
        >
          <NuxtIcon
            name="chevron-line"
            filled
            class="chevron-icon"
            :class="currentPage === totalPages ? 'disabled' : 'active'"
          />
        </button>
      </li>
    </ol>
  </nav>
</template>

<script setup>
const router = useRouter()
const props = defineProps({
  maxVisibleButtons: {
    type: Number,
    required: false,
    default: 5
  },
  totalPages: {
    type: Number,
    required: true
  },
  totalItems: {
    type: Number,
    required: true
  },
  // perPage: {
  // 	type: Number,
  // 	required: true
  // },
  currentPage: {
    type: Number,
    required: true
  }
})

const startPage = () => {
  if (props.currentPage === 1) {
    return 1
  }

  if (props.currentPage === props.totalPages) {
    return props.totalPages - props.maxVisibleButtons + 1
  }

  if (props.currentPage >= props.totalPages - 1) {
    return props.currentPage - 4
  } else if (props.currentPage >= props.totalPages - 2) {
    return props.currentPage - 3
  } else if (props.currentPage >= props.totalPages - 3) {
    return props.currentPage - 2
  } else if (props.currentPage >= props.totalPages - 4) {
    return props.currentPage - 1
  }

  return props.currentPage - 1
}
const endPage = () => {
  return Math.min(startPage() + props.maxVisibleButtons - 1, props.totalPages)
}
const showDots = computed(() => {
  return props.currentPage <= props.totalPages - props.maxVisibleButtons + 1
})
const firstThreePages = () => {
  const range = []

  for (let i = startPage(); i < startPage() + 3; i++) {
    range.push({
      name: i,
      isDisabled: i === props.currentPage
    })
  }

  return range
}

const lastTwoPages = () => {
  const range = []

  for (let i = props.totalPages - 1; i <= props.totalPages; i++) {
    range.push({
      name: i,
      isDisabled: i === props.currentPage
    })
  }

  return range
}
/*
	Below is normal pages list without '...'
*/
const pages = () => {
  const range = []
  for (let i = startPage(); i <= endPage(); i += 1) {
    range.push({
      name: i,
      isDisabled: i === props.currentPage
    })
  }

  return range.filter(page => page.name > 0)
}

// METHODS
// eslint-disable-next-line no-unused-vars
const goToPage = number => {
  router.push({
    query: {
      page: number
    }
  })
}

const emit = defineEmits(['pagechanged'])

const pagechanged = pNumber => {
  emit('pagechanged', pNumber)
}
const nextPage = () => {
  emit('pagechanged', props.currentPage + 1)
}
const previousPage = () => {
  emit('pagechanged', props.currentPage - 1)
}
</script>

<style lang="scss" scoped>
.pagination {
  text-align: center;
  li {
    display: inline-block;
  }
}

:deep(.arrow-icon) {
  svg {
    width: 18px;
    height: 18px;
  }

  @media screen and (min-width: 768px) {
    svg {
      width: 24px;
      height: 24px;
    }
  }
}

:deep(.chevron-icon) {
  svg {
    width: 10px;
    height: 10px;
  }

  @media screen and (min-width: 768px) {
    svg {
      width: 16px;
      height: 16px;
    }
  }
}

:deep(.prev) {
  svg {
    rotate: 180deg;
  }
}

:deep(.disabled) {
  svg {
    path {
      fill: #d2d2d2 !important;
    }
  }
}
:deep(.active) {
  svg {
    path {
      fill: #ff6700 !important;
    }
  }
}
</style>
